const STORAGE_KEY = 'deviceFingerprint';
const PROFILE_ID_KEY = 'tdisprofile-id';

const FONT_LIST = [
  'cursive', 'monospace', 'serif', 'sans-serif', 'fantasy', 'default',
  'Arial', 'Arial Black', 'Arial Narrow', 'Arial Rounded MT Bold',
  'Bookman Old Style', 'Bradley Hand ITC', 'Century', 'Century Gothic',
  'Comic Sans MS', 'Courier', 'Courier New', 'Georgia', 'Gentium',
  'Impact', 'King', 'Lucida Console', 'Lalit', 'Modena',
  'Monotype Corsiva', 'Papyrus', 'Tahoma', 'TeX', 'Times',
  'Times New Roman', 'Trebuchet MS', 'Verdana', 'Verona',
];

const getStoredFingerprint = () => {
  if (typeof window === 'undefined') return '';

  const localValue = window.localStorage?.getItem(STORAGE_KEY);
  if (localValue) return localValue;

  const sessionValue = window.sessionStorage?.getItem(STORAGE_KEY);
  if (sessionValue) return sessionValue;

  return '';
};

const persistFingerprint = value => {
  if (!value || typeof window === 'undefined') return;

  try {
    window.localStorage?.setItem(STORAGE_KEY, value);
  } catch (_) {
    window.sessionStorage?.setItem(STORAGE_KEY, value);
  }
};

const getIdentifier = () => {
  if (typeof window === 'undefined') return '';

  const hasCrypto = window.crypto && typeof window.crypto.getRandomValues === 'function';
  const hasLocalStorage = !!window.localStorage;
  if (!hasCrypto || !hasLocalStorage) return '';

  let identifier = window.localStorage.getItem(PROFILE_ID_KEY);
  if (identifier) return identifier;

  const values = window.crypto.getRandomValues(new Uint32Array(3));
  identifier = `${values[0]}-${values[1]}-${values[2]}`;
  window.localStorage.setItem(PROFILE_ID_KEY, identifier);
  return identifier;
};

const createFontDetector = () => {
  if (typeof document === 'undefined') {
    return { detect: () => false };
  }

  const baseFonts = ['monospace', 'sans-serif', 'serif'];
  const testString = 'mmmmmmmmmmlli';
  const testSize = '72px';
  const body = document.getElementsByTagName('body')[0];

  if (!body) {
    return { detect: () => false };
  }

  const span = document.createElement('span');
  span.style.fontSize = testSize;
  span.innerHTML = testString;

  const defaultWidth = {};
  const defaultHeight = {};

  for (let i = 0; i < baseFonts.length; i += 1) {
    const font = baseFonts[i];
    span.style.fontFamily = font;
    body.appendChild(span);
    defaultWidth[font] = span.offsetWidth;
    defaultHeight[font] = span.offsetHeight;
    body.removeChild(span);
  }

  return {
    detect: font => {
      let detected = false;

      for (let i = 0; i < baseFonts.length; i += 1) {
        const baseFont = baseFonts[i];
        span.style.fontFamily = `${font},${baseFont}`;
        body.appendChild(span);
        const matched = span.offsetWidth !== defaultWidth[baseFont]
          || span.offsetHeight !== defaultHeight[baseFont];
        body.removeChild(span);
        detected = detected || matched;
      }

      return detected;
    },
  };
};

const collectBrowserFontsInfo = () => {
  const detector = createFontDetector();
  const installedFonts = [];

  for (let i = 0; i < FONT_LIST.length; i += 1) {
    if (detector.detect(FONT_LIST[i])) {
      installedFonts.push(FONT_LIST[i]);
    }
  }

  return {
    installedFonts: installedFonts.join(';') + (installedFonts.length ? ';' : ''),
  };
};

const collectBrowserPluginsInfo = () => {
  if (typeof navigator === 'undefined' || !navigator.plugins) {
    return {};
  }

  const pluginNames = [];
  for (let i = 0; i < navigator.plugins.length; i += 1) {
    pluginNames.push(navigator.plugins[i].filename);
  }

  return {
    installedPlugins: pluginNames.join(';') + (pluginNames.length ? ';' : ''),
  };
};

const collectScreenInfo = () => {
  const screenDetails = typeof window !== 'undefined' ? window.screen : undefined;
  if (!screenDetails) return {};

  const screenInfo = {};
  if (screenDetails.width) screenInfo.screenWidth = screenDetails.width;
  if (screenDetails.height) screenInfo.screenHeight = screenDetails.height;
  if (screenDetails.pixelDepth) screenInfo.screenColourDepth = screenDetails.pixelDepth;
  return screenInfo;
};

const collectTimezoneInfo = () => {
  const offset = new Date().getTimezoneOffset();
  return { timezone: offset };
};

const collectNavigatorInfo = () => {
  if (typeof navigator === 'undefined') return {};

  const nav = navigator;
  const fields = [
    'userAgent',
    'appName',
    'appCodeName',
    'appVersion',
    'appMinorVersion',
    'buildID',
    'platform',
    'cpuClass',
    'oscpu',
    'product',
    'productSub',
    'vendor',
    'vendorSub',
    'language',
    'userLanguage',
    'browserLanguage',
    'systemLanguage',
  ];

  const details = {};
  for (let i = 0; i < fields.length; i += 1) {
    const field = fields[i];
    if (nav[field]) {
      details[field] = nav[field];
    }
  }

  return details;
};

const buildDevicePrint = () => {
  const devicePrint = {
    screen: collectScreenInfo(),
    timezone: collectTimezoneInfo(),
    plugins: collectBrowserPluginsInfo(),
    fonts: collectBrowserFontsInfo(),
    identifier: getIdentifier(),
    ...collectNavigatorInfo(),
  };

  return JSON.stringify(devicePrint);
};

export const getOrCreateDeviceFingerprint = async () => {
  const existing = getStoredFingerprint();
  if (existing) return existing;

  const devicePrintValue = buildDevicePrint();
  persistFingerprint(devicePrintValue);
  return devicePrintValue;
};
